import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getCollectionById, getCollectionWorkspacesByCollectionId } from "@/db/collections"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.collections', supabase)

type Row = Tables<'collections'>
type WsRow = Tables<'collection_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const collectionWorkspaces = await getCollectionWorkspacesByCollectionId(supabase, id)
	return collectionWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setCollections
 * @returns
 */
export function makeCollectionChangeHandler(
	setCollections: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `collection-changes-${uuid}`,
		channelName: `collection-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'collections',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const collection = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setCollections(prev => {
					const exists = prev.some(p => p.id === collection.id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling collection update:${printObject(collection)}`)
					return prev.map(p => p.id === collection.id ? collection : p)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setCollections
 * @returns
 */
export function makeCollectionWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setCollections: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `collection-ws-changes-${uuid}`,
		channelName: `collection-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'collection_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const collectionWs = payload.new as WsRow
				const id = collectionWs.collection_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const collection = await getCollectionById(supabase, id)
					if (collection) {
						setCollections(prev => {
 							if (prev.some(p => p.id === collection.id)) return prev
							logger.info(`Handling collection Insert:${printObject(collection)}`)
 							return [...prev, collection]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const collectionWs = payload.old as WsRow
				const id = collectionWs.collection_id
				setCollections(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling collection Delete:${id}`)
					return prev.filter(p => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}